/**
 * Quran Lexical Explorer — three pages exported here:
 *   /lexical/lemma/:lemma  → LemmaDetail
 *   /lexical/root/:root    → RootDetail
 *   /lexical/search?q=...  → LexicalSearchPage
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search as SearchIcon,
  AlertCircle,
  ChevronRight,
  BookOpen,
  GitBranch,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { lexicalApi } from '../services/api';
import { usePreferences } from '../context/PreferencesContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { Badge } from '../components/ui/badge';

const NotImported = ({ children }) => (
  <div className="flex items-start gap-2 p-4 rounded-md bg-amber-900/20 border border-amber-700/40 text-amber-200 text-sm">
    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div>
      {children || (
        <>
          Aucune donnée lexicale trouvée. Importez via{' '}
          <code className="text-xs bg-black/30 px-1 rounded">scripts/import_morphology.py</code>.
        </>
      )}
    </div>
  </div>
);

const GlossBlock = ({ gloss }) => {
  if (!gloss || !gloss.shortMeaning) {
    return (
      <div className="p-2 bg-surface-elevated rounded text-sm text-text-muted italic" data-testid="gloss-unavailable">
        <p>📝 Définition française non disponible</p>
        <p className="text-xs mt-1 opacity-75">Les définitions manquantes peuvent être générées automatiquement (voir la documentation)</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="gloss-block">
      <p className="text-base text-text-primary font-medium">{gloss.shortMeaning}</p>
      {gloss.definition && (
        <p className="text-sm text-text-secondary leading-relaxed">{gloss.definition}</p>
      )}
      {gloss.source?.provider && (
        <p className="text-[11px] text-text-muted">
          Source : {gloss.source.provider}
          {gloss.source.licenseStatus && ` · ${gloss.source.licenseStatus}`}
        </p>
      )}
    </div>
  );
};

const OccurrenceList = ({ items, label }) => {
  if (!items?.length) return null;

  return (
    <div className="space-y-2" data-testid={`occurrence-list-${label || 'default'}`}>
      <h3 className="text-sm uppercase tracking-wider text-text-muted">
        Occurrences ({items.length})
      </h3>
      <div className="space-y-1.5">
        {items.map((occ) => {
          // Build proper ayahKey from available data
          // Handle both camelCase and snake_case variants
          const surahNum = occ.surahNumber || occ.surah_number || occ.surah;
          const ayahNum = occ.ayahNumber || occ.ayah_number || occ.ayah;
          const ayahKey = occ.ayahKey || occ.verse_key || (surahNum && ayahNum ? `${surahNum}:${ayahNum}` : 'N/A');
          const textUthmani = occ.textUthmani || occ.word_original;
          
          // Build link with proper routing - use hash for auto-scroll + focus
          const linkPath = surahNum && ayahNum 
            ? `/quran/${surahNum}#verse-${ayahNum}`
            : '#';

          return (
            <Link
              key={occ.id || occ._id || `${ayahKey}-${occ.position}`}
              to={linkPath}
              className="flex flex-col gap-2 p-3 rounded hover:bg-surface-elevated transition-colors group border border-border/50 hover:border-primary/30"
              data-testid={`occurrence-${occ.id || ayahKey}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-semibold bg-primary/10 border-primary/30">
                    {ayahKey}
                  </Badge>
                  {occ.partOfSpeech && (
                    <Badge variant="secondary" className="text-xs">
                      {occ.partOfSpeech}
                    </Badge>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-primary flex-shrink-0" />
              </div>
              
              {/* Display the verse text with the found word highlighted */}
              <div className="space-y-1">
                {occ.ayahText ? (
                  <div className="font-arabic text-right text-sm text-text-secondary leading-relaxed" dir="rtl">
                    {occ.ayahText}
                  </div>
                ) : (
                  <div className="text-xs text-text-muted italic">
                    (texte du verset non disponible)
                  </div>
                )}
                
                {/* French translation */}
                {occ.translation?.fr && (
                  <div className="text-sm text-text-secondary italic">{occ.translation.fr}</div>
                )}
                
                {textUthmani && (
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <span className="font-arabic text-primary font-semibold" dir="rtl">
                      {textUthmani}
                    </span>
                    <span>← mot trouvé</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Lemma detail ----------
export const LemmaDetail = () => {
  const { lemma } = useParams();
  const navigate = useNavigate();
  const { language } = usePreferences();
  const [data, setData] = useState(null);
  const [occ, setOcc] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      lexicalApi.getLemma(lemma, language),
      lexicalApi.getLemmaOccurrences(lemma, 1, 100),
    ])
      .then(([d, o]) => {
        setData(d.data);
        setOcc(o.data?.items || []);
      })
      .catch((e) =>
        setError(e?.response?.status === 404 ? 'Lemme non trouvé.' : 'Erreur de chargement'),
      )
      .finally(() => setLoading(false));
  }, [lemma, language]);

  if (loading) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lemma-detail-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>

      <SectionHeader
        overline="Lemme"
        title={data?.lemma || lemma}
        subtitle={
          data
            ? `${data.occurrenceCount}× dans le Coran · ${data.forms?.length || 0} formes attestées`
            : ''
        }
      />

      {error ? (
        <NotImported>{error}</NotImported>
      ) : data && (
        <div className="space-y-6">
          <GlassCard data-testid="lemma-card">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted">Forme arabe</div>
                <div className="font-arabic text-3xl text-text-primary mt-1" dir="rtl">
                  {data.lemma}
                </div>
              </div>
              {data.root && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-text-muted">Racine</div>
                  <Link
                    to={`/lexical/root/${encodeURIComponent(data.normalizedRoot || data.root)}`}
                    className="font-arabic text-2xl text-primary hover:underline tracking-widest mt-1 inline-block"
                    dir="rtl"
                  >
                    {data.root}
                  </Link>
                </div>
              )}
              {data.partOfSpeech && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-text-muted">
                    Type grammatical
                  </div>
                  <Badge className="mt-1 bg-primary/15 text-primary border-primary/30">
                    {data.partOfSpeech}
                  </Badge>
                </div>
              )}
            </div>
            <div className="mt-5 pt-5 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
                Définition ({language.toUpperCase()})
              </div>
              <GlossBlock gloss={data.gloss} />
            </div>
            {data.forms?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
                  Formes attestées
                </div>
                <div className="flex flex-wrap gap-1.5" dir="rtl">
                  {data.forms.map((f) => (
                    <Badge key={f} variant="outline" className="font-arabic text-base">
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>

          <GlassCard>
            <OccurrenceList items={occ} label="lemma" />
          </GlassCard>
        </div>
      )}
    </div>
  );
};

// ---------- Root detail ----------
export const RootDetail = () => {
  const { root } = useParams();
  const navigate = useNavigate();
  const { language } = usePreferences();
  const [data, setData] = useState(null);
  const [occ, setOcc] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      lexicalApi.getRoot(root, language),
      lexicalApi.getRootOccurrences(root, 1, 100),
    ])
      .then(([d, o]) => {
        setData(d.data);
        setOcc(o.data?.items || []);
      })
      .catch((e) =>
        setError(e?.response?.status === 404 ? 'Racine non trouvée.' : 'Erreur de chargement'),
      )
      .finally(() => setLoading(false));
  }, [root, language]);

  if (loading) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="root-detail-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>

      <SectionHeader
        overline="Racine"
        title={data?.root || root}
        subtitle={
          data
            ? `${data.occurrenceCount}× dans le Coran · ${data.lemmas?.length || 0} lemmes dérivés`
            : ''
        }
      />

      {error ? (
        <NotImported>{error}</NotImported>
      ) : data && (
        <div className="space-y-6">
          <GlassCard>
            <div className="font-arabic text-4xl text-text-primary text-center tracking-widest" dir="rtl">
              {data.root}
            </div>
            <div className="mt-5 pt-5 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
                Sens principal ({language.toUpperCase()})
              </div>
              <GlossBlock gloss={data.gloss} />
            </div>
            {data.lemmas?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
                  Lemmes dérivés
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.lemmas.map((l) => (
                    <Link key={l} to={`/lexical/lemma/${encodeURIComponent(l)}`}>
                      <Badge
                        variant="outline"
                        className="font-arabic text-base hover:bg-primary/10 hover:border-primary/40"
                      >
                        {l}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>

          <GlassCard>
            <OccurrenceList items={occ} label="root" />
          </GlassCard>
        </div>
      )}
    </div>
  );
};

// ---------- Search ----------
const ResultGroup = ({ title, count, children, testid }) => {
  if (!count) return null;
  return (
    <GlassCard className="mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm uppercase tracking-wider text-text-muted">{title}</h3>
        <Badge variant="outline">{count}</Badge>
      </div>
      {children}
    </GlassCard>
  );
};

export const LexicalSearchPage = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const mode = params.get('mode') || 'all';
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q) {
      setData(null);
      return;
    }
    setLoading(true);
    lexicalApi
      .search(q, mode, 1, 25)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [q, mode]);

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setParams({ q: input.trim(), mode });
  };

  const total = useMemo(() => {
    if (!data) return 0;
    return (
      (data.results?.exactWord?.total || 0)
      + (data.results?.lemmas?.total || 0)
      + (data.results?.roots?.total || 0)
    );
  }, [data]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lexical-search-page">
      <SectionHeader
        overline="Explorateur lexical"
        title="Recherche : mot · lemme · racine"
        subtitle="Saisissez en arabe (avec ou sans harakāt). La normalisation gère automatiquement les variantes alif / shadda / ya / ta-marbuta."
      />

      <form onSubmit={submit} className="flex flex-wrap gap-2 mb-4" data-testid="lex-search-form">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ex : الله · الصراط · خلق · ر ح م"
          dir="auto"
          className="bg-surface border-border flex-1 min-w-[200px] font-arabic text-lg"
          data-testid="lexical-search-input"
        />
        <select
          value={mode}
          onChange={(e) => setParams({ q, mode: e.target.value })}
          className="h-10 px-3 rounded-md bg-surface border border-border text-sm"
          data-testid="lexical-search-mode"
        >
          <option value="all">Tous</option>
          <option value="exact_word">Mot exact</option>
          <option value="lemma">Lemme</option>
          <option value="root">Racine</option>
        </select>
        <Button type="submit" className="bg-primary text-background hover:bg-primary-hover">
          <SearchIcon className="w-4 h-4 mr-2" /> {t('common.search')}
        </Button>
      </form>

      {loading && <PageLoading />}

      {!loading && data && (
        <>
          <p className="text-xs text-text-muted mb-4" data-testid="lex-search-meta">
            Recherche : <span className="font-arabic text-base text-text-primary">{data.query}</span>{' '}
            · forme normalisée :{' '}
            <code className="text-xs bg-black/30 px-1 rounded">{data.normalizedQuery}</code> ·{' '}
            <span className="text-primary">{total}</span> résultat(s)
          </p>

          <ResultGroup
            title="Lemmes"
            count={data.results?.lemmas?.total || 0}
            testid="lex-search-lemmas"
          >
            <div className="flex flex-wrap gap-2">
              {data.results.lemmas.items.map((l) => (
                <Link
                  key={l.id || l.normalizedLemma}
                  to={`/lexical/lemma/${encodeURIComponent(l.normalizedLemma)}`}
                  data-testid={`lex-lemma-${l.normalizedLemma}`}
                >
                  <Badge
                    variant="outline"
                    className="font-arabic text-lg px-3 py-1.5 hover:bg-primary/10 hover:border-primary/40"
                  >
                    {l.lemma}
                    <span className="ml-2 text-xs text-text-muted">
                      ({l.occurrenceCount})
                    </span>
                  </Badge>
                </Link>
              ))}
            </div>
          </ResultGroup>

          <ResultGroup
            title="Racines"
            count={data.results?.roots?.total || 0}
            testid="lex-search-roots"
          >
            <div className="flex flex-wrap gap-2">
              {data.results.roots.items.map((r) => (
                <Link
                  key={r.id || r.normalizedRoot}
                  to={`/lexical/root/${encodeURIComponent(r.normalizedRoot)}`}
                  data-testid={`lex-root-${r.normalizedRoot}`}
                >
                  <Badge
                    variant="outline"
                    className="font-arabic text-lg px-3 py-1.5 tracking-widest hover:bg-primary/10 hover:border-primary/40"
                  >
                    {r.root}
                    <span className="ml-2 text-xs text-text-muted">
                      ({r.occurrenceCount})
                    </span>
                  </Badge>
                </Link>
              ))}
            </div>
          </ResultGroup>

          <ResultGroup
            title="Mot exact dans les versets"
            count={data.results?.exactWord?.total || 0}
            testid="lex-search-exact"
          >
            <OccurrenceList items={data.results.exactWord.items} label="exact" />
          </ResultGroup>

          {data.suggestions?.length > 0 && (
            <div
              className="mt-4 p-4 rounded-lg border border-primary/30 bg-primary/5"
              data-testid="lex-suggestions-block"
            >
              <div className="text-sm text-text-secondary mb-2">
                {total === 0
                  ? <>Aucun résultat pour <span className="font-arabic">{data.query}</span>. Essayez :</>
                  : <>Vouliez-vous explorer le lemme ou la racine coranique ?</>}
              </div>
              <div className="flex flex-wrap gap-2">
                {data.suggestions.map((s, i) => (
                  <button
                    key={`${s.type}-${s.normalizedValue}-${i}`}
                    type="button"
                    onClick={() => {
                      const target = s.type === 'lemma'
                        ? `/lexical/lemma/${encodeURIComponent(s.normalizedValue)}`
                        : `/lexical/root/${encodeURIComponent(s.normalizedValue)}`;
                      window.location.assign(target);
                    }}
                    className="text-xs"
                    data-testid={`lex-suggestion-${i}`}
                  >
                    <Badge
                      variant="outline"
                      className="font-arabic text-base px-3 py-1.5 hover:bg-primary/10 hover:border-primary/50"
                    >
                      {s.value}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-text-muted">
                        {s.type}
                        {typeof s.occurrenceCount === 'number' ? ` · ${s.occurrenceCount}×` : ''}
                      </span>
                    </Badge>
                  </button>
                ))}
              </div>
            </div>
          )}

          {total === 0 && !data.suggestions?.length && (
            <NotImported>
              Aucun résultat pour <span className="font-arabic">{data.query}</span>.
            </NotImported>
          )}
        </>
      )}

      {!loading && !data && q && <NotImported />}

      {!q && (
        <GlassCard className="text-center py-10">
          <SearchIcon className="w-10 h-10 text-primary/60 mx-auto mb-3" />
          <p className="text-sm text-text-secondary max-w-md mx-auto">
            Saisissez un mot, un lemme ou une racine pour explorer la morphologie du Coran.
            La recherche normalise harakāt, alif, shadda et ya automatiquement.
          </p>
        </GlassCard>
      )}
    </div>
  );
};
